import { FC, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { useCrewDashboard } from './dashboard/hooks/useCrewDashboard'
import KpiCards from './dashboard/KpiCards'
import CrewComposition from './dashboard/CrewComposition'
import SignOnOffTable from './dashboard/SignOnOffTable'
import PendingActions from './dashboard/PendingActions'
import {
  KpiDetailModal,
  SignOnOffModal,
  PendingActionsModal,
  CertificationModal,
  RestHourModal,
  CrewChangeModal,
  TrainingModal,
  OpenPositionsModal,
} from './dashboard/DashboardModals'
import CardInfoButton from './dashboard/CardInfoButton'
import type { CardMeta, ModalType } from './dashboard/types'
import { Users, Ship, FileText, ClipboardList, CalendarPlus, UserPlus, Settings } from 'lucide-react'
import { KpiCardSkeleton, CardSkeleton } from '../../../shared/components/LoadingSkeletons'

/* ─── Timestamp ─── */
const nowStr = () => {
  const d = new Date()
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ', ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) + ' UTC'
}

/* ─── Quick Action Links ─── */
interface QuickAction {
  label: string
  sub: string
  route: string
  icon: FC<{ className?: string }>
}

const quickActions: QuickAction[] = [
  { label: 'Crew Master', sub: 'Manage crew records', route: '/crewing/overview', icon: Users },
  { label: 'Sign-On/Off', sub: 'Manage signings', route: '/crewing/signing', icon: Ship },
  { label: 'Assignments', sub: 'Crew assignments', route: '/crewing/assignment', icon: ClipboardList },
  { label: 'Certifications', sub: 'Track certificates', route: '/crewing/overview', icon: FileText },
  { label: 'Schedule Change', sub: 'Plan rotations', route: '/crewing/signing', icon: CalendarPlus },
  { label: 'Add Crew', sub: 'New crew member', route: '/crewing/overview', icon: UserPlus },
  { label: 'Settings', sub: 'Crewing config', route: '/crewing/overview', icon: Settings },
]

const actionRoutes: Record<string, string> = {
  'Crew Master': '/crewing/overview',
  'Sign-On/Off': '/crewing/signing',
  'Assignments': '/crewing/assignment',
  'Certifications': '/crewing/overview',
  'Schedule Change': '/crewing/signing',
  'Add Crew': '/crewing/overview',
  'Settings': '/crewing/overview',
}

/* ─── Card Meta for Sidebar ─── */
const sidebarMeta: Record<string, CardMeta> = {
  quickLinks: {
    description: 'Quick access to common crew management pages.',
    endpoint: 'N/A — Internal navigation links',
    logic: 'Clicking a link opens a confirmation modal. "Go to page" navigates to the target route.',
  },
}

/* ─── Main Component ─── */
const CrewDashboard: FC = () => {
  const navigate = useNavigate()
  const { loading, error, kpis, summary, crewList, signOnOffRecords, ranks, vessels, trainings, refresh } = useCrewDashboard()

  const [modal, setModal] = useState<ModalType>(null)
  const [actionModal, setActionModal] = useState<string | null>(null)

  /* ─── Derived chart data ─── */
  const deptChartData = useMemo(() => {
    if (summary.crewByDepartment.length === 0) {
      return [{ department: 'Deck', count: 12 }, { department: 'Engine', count: 8 }, { department: 'Galley', count: 3 }, { department: 'Cabin', count: 2 }]
    }
    return summary.crewByDepartment
  }, [summary.crewByDepartment])

  const rankChartData = useMemo(() => {
    if (summary.crewByRank.length === 0) {
      return [
        { rankName: 'Master', count: 1 },
        { rankName: 'Chief Officer', count: 1 },
        { rankName: '2nd Officer', count: 1 },
        { rankName: 'Chief Engineer', count: 1 },
        { rankName: '2nd Engineer', count: 1 },
        { rankName: 'Able Seaman', count: 3 },
        { rankName: 'Oiler', count: 2 },
      ]
    }
    return summary.crewByRank
  }, [summary.crewByRank])

  /* ─── Pending actions (derived) ─── */
  const pendingActions = useMemo(() => {
    const actions: { label: string; desc: string; color: 'warning' | 'danger' | 'success' }[] = []
    if (summary.pendingApprovals > 0) {
      actions.push({
        label: 'Pending Approvals',
        desc: `${summary.pendingApprovals} crew member(s) awaiting approval`,
        color: 'warning',
      })
    }
    if (summary.certificationsExpiringSoon > 0) {
      actions.push({
        label: 'Certifications',
        desc: `${summary.certificationsExpiringSoon} document(s) expiring or needing attention`,
        color: 'danger',
      })
    }
    if (actions.length === 0) {
      actions.push({
        label: 'All Clear',
        desc: 'No pending actions at this time',
        color: 'success',
      })
    }
    return actions
  }, [summary])

  /* ─── Modal handlers ─── */
  const handleKpiClick = (kpi: string) => {
    if (kpi === 'expiringCerts') { setModal('certification'); return }
    if (kpi === 'signedOff' || kpi === 'onboardNow') { setModal('signOnOff'); return }
    setModal('kpiDetail')
  }

  const handleActionClick = (label: string) => {
    if (label === 'Pending Approvals') { setModal('pendingActions'); return }
    if (label === 'Certifications') { setModal('certification'); return }
  }

  if (loading) {
    return (
      <div className='flex flex-col flex-1 min-h-0'>
        <div className='flex flex-col flex-1 px-5 xl:px-10 pt-5 pb-8 gap-5 md:gap-6'>
          {/* Header Skeleton */}
          <div className='flex justify-between items-center'>
            <div>
              <div className='placeholder-wave'>
                <div className='placeholder rounded' style={{ width: 150, height: 22 }} />
              </div>
              <div className='placeholder-wave mt-1'>
                <div className='placeholder rounded' style={{ width: 180, height: 12 }} />
              </div>
            </div>
            <div className='placeholder-wave'>
              <div className='placeholder rounded' style={{ width: 60, height: 16 }} />
            </div>
          </div>

          {/* Quick Actions Skeleton */}
          <div>
            <div className='placeholder-wave mb-3'>
              <div className='placeholder rounded' style={{ width: 90, height: 12 }} />
            </div>
            <div className='grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3'>
              {Array.from({ length: 7 }).map((_, i) => (
                <div key={i} className='flex flex-col items-center gap-2 p-3 bg-white rounded-xl border border-slate-200 shadow-sm'>
                  <div className='placeholder-wave'>
                    <div className='placeholder rounded-lg' style={{ width: 32, height: 32 }} />
                  </div>
                  <div className='placeholder-wave w-full flex justify-center'>
                    <div className='placeholder rounded' style={{ width: '70%', height: 12 }} />
                  </div>
                  <div className='placeholder-wave w-full flex justify-center'>
                    <div className='placeholder rounded' style={{ width: '90%', height: 10 }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* KPI Cards Skeleton */}
          <div className='grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-5 md:gap-6'>
            {Array.from({ length: 6 }).map((_, i) => (
              <KpiCardSkeleton key={i} />
            ))}
          </div>

          {/* Section 1: Charts Skeleton */}
          <div className='grid grid-cols-1 xl:grid-cols-12 gap-5 md:gap-6'>
            <div className='xl:col-span-7 flex flex-col'>
              <div className='bg-white rounded-2xl p-5 border border-slate-200 shadow-sm'>
                <div className='placeholder-wave mb-4'>
                  <div className='placeholder rounded' style={{ width: '30%', height: 16 }} />
                </div>
                <div className='placeholder-wave'>
                  <div className='placeholder rounded-xl w-full' style={{ height: 220 }} />
                </div>
              </div>
            </div>
            <div className='xl:col-span-5 flex flex-col'>
              <div className='bg-white rounded-2xl p-5 border border-slate-200 shadow-sm'>
                <div className='placeholder-wave mb-4'>
                  <div className='placeholder rounded' style={{ width: '40%', height: 16 }} />
                </div>
                <div className='placeholder-wave'>
                  <div className='placeholder rounded-xl w-full' style={{ height: 220 }} />
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: Tables & Pending Actions Skeleton */}
          <div className='grid grid-cols-1 xl:grid-cols-12 gap-5 md:gap-6'>
            <div className='xl:col-span-8 flex flex-col'>
              <div className='bg-white rounded-2xl p-5 border border-slate-200 shadow-sm'>
                <div className='placeholder-wave mb-4'>
                  <div className='placeholder rounded' style={{ width: '25%', height: 16 }} />
                </div>
                <div className='space-y-3'>
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className='placeholder-wave'>
                      <div className='placeholder rounded w-full' style={{ height: 36 }} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className='xl:col-span-4 flex flex-col'>
              <CardSkeleton lines={4} />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='flex flex-col flex-1 min-h-0'>
      <div className='flex flex-col flex-1 px-5 xl:px-10 pt-5 pb-8 gap-5 md:gap-6'>

        {/* ── Page Header ── */}
        <div className='flex justify-between items-center'>
          <div>
            <h3 className='text-lg font-bold text-slate-900'>Crew Dashboard</h3>
            <div className='text-[11px] text-slate-400'>{nowStr()}</div>
          </div>
          <button
            onClick={refresh}
            disabled={loading}
            className='text-[11px] text-blue-600 hover:text-blue-800 font-semibold transition-colors disabled:opacity-50 flex items-center gap-1.5'
          >
            <span className={loading ? 'inline-block animate-spin' : ''}>↻</span>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {/* ── Error Banner ── */}
        {error && (
          <div className='flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700'>
            <div className='flex items-center gap-2'>
              <span className='font-bold'>Notice:</span>
              <span>{error}</span>
            </div>
            <button
              onClick={refresh}
              disabled={loading}
              className='font-semibold text-red-800 underline hover:no-underline ml-4 disabled:opacity-50'
            >
              Retry
            </button>
          </div>
        )}

        {/* ── Quick Actions Grid ── */}
        <div>
          <div className='flex items-center gap-2 mb-3'>
            <h4 className='text-xs font-semibold text-slate-500 uppercase tracking-wider'>Quick Actions</h4>
            <CardInfoButton meta={sidebarMeta.quickLinks} />
          </div>
          <div className='grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3'>
            {quickActions.map((action, i) => {
              const Icon = action.icon
              return (
                <button key={i}
                  onClick={() => setActionModal(action.label)}
                  className='flex flex-col items-center gap-1.5 p-3 bg-white rounded-xl border border-slate-200 shadow-sm hover:border-blue-300 hover:shadow-md transition-all text-center'
                >
                  <div className='w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center'>
                    <Icon className='w-4 h-4 text-blue-600' />
                  </div>
                  <div className='text-[11px] font-semibold text-slate-800 leading-tight'>{action.label}</div>
                  <div className='text-[9px] text-slate-400 leading-tight'>{action.sub}</div>
                </button>
              )
            })}
          </div>
        </div>

        {/* ── KPI Cards ── */}
        <KpiCards loading={loading} kpis={kpis} onKpiClick={handleKpiClick} />

        {/* ── Section 1: Charts ── */}
        <div className='grid grid-cols-1 xl:grid-cols-12 gap-5 md:gap-6'>
          <CrewComposition deptChartData={deptChartData} rankChartData={rankChartData} />
        </div>

        {/* ── Section 2: Tables ── */}
        <div className='grid grid-cols-1 xl:grid-cols-12 gap-5 md:gap-6'>
          <div className='xl:col-span-8 flex flex-col'>
            <SignOnOffTable
              records={summary.recentSignOnOff}
              onViewAll={() => setModal('signOnOff')}
            />
          </div>
          <div className='xl:col-span-4 flex flex-col'>
            <PendingActions items={pendingActions} onActionClick={handleActionClick} />
          </div>
        </div>

        {/* ── Section 3: Training Pipeline ── */}
        <div className='grid grid-cols-1 xl:grid-cols-12 gap-5 md:gap-6'>
          <div className='xl:col-span-8 flex flex-col'>
            <div className='flex flex-col flex-1 bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-5'>
              <div className='flex items-center justify-between mb-3'>
                <h6 className='text-sm font-bold text-slate-900'>Training Pipeline</h6>
                <button onClick={() => setModal('training')} className='text-[10px] font-semibold text-blue-600 hover:text-blue-800 transition-colors'>
                  View all
                </button>
              </div>
              <div className='overflow-x-auto max-h-[220px] overflow-y-auto'>
                <table className='w-full text-[11px]'>
                  <thead>
                    <tr className='text-slate-400 text-[10px] uppercase tracking-wider'>
                      <th className='text-left font-semibold py-2 pr-2'>Crew</th>
                      <th className='text-left font-semibold py-2 px-2'>Topic</th>
                      <th className='text-left font-semibold py-2 px-2'>Type</th>
                      <th className='text-right font-semibold py-2 pl-2'>Status</th>
                    </tr>
                  </thead>
                  <tbody className='divide-y divide-slate-100'>
                    {trainings.length === 0 ? (
                      <tr>
                        <td colSpan={4} className='text-center text-slate-400 py-8 text-xs'>No training records found</td>
                      </tr>
                    ) : (
                      trainings.slice(0, 5).map((t: any, i: number) => (
                        <tr key={i} className='hover:bg-slate-50 transition-colors'>
                          <td className='py-2.5 pr-2 font-semibold text-slate-800'>{t.crewName || '—'}</td>
                          <td className='py-2.5 px-2 text-slate-700'>{t.topic || '—'}</td>
                          <td className='py-2.5 px-2 text-slate-500'>{t.trainingType || '—'}</td>
                          <td className='py-2.5 pl-2 text-right'>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${
                              t.workflowStatus === 'REVIEWED' ? 'bg-emerald-50 text-emerald-600' :
                              t.workflowStatus === 'REJECTED' ? 'bg-red-50 text-red-600' :
                              'bg-amber-50 text-amber-600'
                            }`}>{t.workflowStatus || 'DRAFT'}</span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className='xl:col-span-4 flex flex-col space-y-4'>
            {/* Rest Hour Compliance */}
            <div className='flex flex-col flex-1 bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-5 cursor-pointer hover:shadow-md transition-all' onClick={() => setModal('restHour')}>
              <h6 className='text-sm font-bold text-slate-900 mb-2'>Rest Hour Compliance</h6>
              <p className='text-[10px] text-slate-400 flex-1 flex items-center justify-center py-4'>No compliance data yet</p>
            </div>

            {/* Upcoming Crew Changes */}
            <div className='flex flex-col flex-1 bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-5 cursor-pointer hover:shadow-md transition-all' onClick={() => setModal('crewChange')}>
              <h6 className='text-sm font-bold text-slate-900 mb-2'>Upcoming Crew Changes</h6>
              <p className='text-[10px] text-slate-400 flex-1 flex items-center justify-center py-4'>No upcoming changes</p>
            </div>

            {/* Open Positions */}
            <div className='flex flex-col flex-1 bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-5 cursor-pointer hover:shadow-md transition-all' onClick={() => setModal('openPositions')}>
              <h6 className='text-sm font-bold text-slate-900 mb-2'>Open Positions</h6>
              <p className='text-[10px] text-slate-400 flex-1 flex items-center justify-center py-4'>No vacant positions</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Action Confirmation Modal ── */}
      {actionModal && (
        <div className='fixed inset-0 z-[1050] flex items-center justify-center p-4'>
          <div className='fixed inset-0 bg-black/40' onClick={() => setActionModal(null)} />
          <div className='relative bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-sm p-6'>
            <h3 className='text-sm font-bold text-slate-900 mb-2'>Navigate to {actionModal}?</h3>
            <p className='text-[11px] text-slate-500 mb-4'>This action will redirect you to the {actionModal} page.</p>
            <div className='flex justify-end gap-2'>
              <button onClick={() => setActionModal(null)} className='px-4 py-2 text-[11px] font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors'>
                Cancel
              </button>
              <button onClick={() => { setActionModal(null); navigate(actionRoutes[actionModal] || '/crewing/overview') }}
                className='px-4 py-2 text-[11px] font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors'>
                Go to page
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modals ── */}
      <KpiDetailModal open={modal === 'kpiDetail'} onClose={() => setModal(null)} kpis={kpis} />
      <SignOnOffModal open={modal === 'signOnOff'} onClose={() => setModal(null)} records={signOnOffRecords} />
      <PendingActionsModal open={modal === 'pendingActions'} onClose={() => setModal(null)} items={pendingActions} />
      <CertificationModal open={modal === 'certification'} onClose={() => setModal(null)} crewList={crewList} />
      <TrainingModal open={modal === 'training'} onClose={() => setModal(null)} trainings={trainings} />
      <RestHourModal open={modal === 'restHour'} onClose={() => setModal(null)} />
      <CrewChangeModal open={modal === 'crewChange'} onClose={() => setModal(null)} />
      <OpenPositionsModal open={modal === 'openPositions'} onClose={() => setModal(null)} />
      <ToastContainer position='top-center' style={{ top: '5rem' }} />
    </div>
  )
}

export default CrewDashboard
